import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
} from '@dnd-kit/core';
import {
    useDraggable,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';

import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
    Save, RotateCcw, Plus, Trash2, Settings, Grid, Move,
    Users, Sun, Sofa, Wine, RefreshCw, Lock, Unlock, ChefHat
} from 'lucide-react';
import { cn } from '../lib/utils';

// Zone configuration
const ZONES = [
    { id: 'terrasse', name: 'Terrasse', color: 'bg-amber-500', icon: Sun },
    { id: 'salle', name: 'Salle', color: 'bg-blue-500', icon: Sofa },
    { id: 'bar', name: 'Bar', color: 'bg-purple-500', icon: Wine },
];

// Status colors
const STATUS_COLORS = {
    libre: 'bg-emerald-500',
    free: 'bg-emerald-500',
    available: 'bg-emerald-500',
    occupee: 'bg-orange-500',
    occupied: 'bg-orange-500',
    a_debarrasser: 'bg-amber-500',
    reservee: 'bg-yellow-500',
};

// Draggable Table Component
const DraggableTable = ({ table, isEditMode, onSelect, isSelected }) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: table.id,
        disabled: !isEditMode,
    });

    const style = transform ? {
        transform: CSS.Translate.toString(transform),
        zIndex: isDragging ? 1000 : 1,
    } : undefined;

    const statusColor = STATUS_COLORS[table.status?.toLowerCase()] || 'bg-gray-400';

    return (
        <div
            ref={setNodeRef}
            style={{
                ...style,
                position: 'absolute',
                left: table.position_x || 0,
                top: table.position_y || 0,
            }}
            {...(isEditMode ? { ...listeners, ...attributes } : {})}
            onClick={() => !isEditMode && onSelect?.(table)}
            className={cn(
                "w-16 h-16 rounded-xl border-2 flex flex-col items-center justify-center cursor-pointer transition-all shadow-md",
                isDragging && "opacity-50 scale-110",
                isSelected && "ring-4 ring-blue-500 ring-offset-2",
                isEditMode ? "cursor-move hover:scale-105" : "hover:scale-105",
                table.zone === 'terrasse' && "bg-amber-50 border-amber-400",
                table.zone === 'salle' && "bg-blue-50 border-blue-400",
                table.zone === 'bar' && "bg-purple-50 border-purple-400",
                !table.zone && "bg-gray-50 border-gray-400"
            )}
            data-testid={`floor-table-${table.number}`}
        >
            {/* Status indicator */}
            <div className={cn("absolute -top-1 -right-1 w-3 h-3 rounded-full", statusColor)} />
            
            {/* Table number */}
            <span className="text-lg font-bold text-gray-800">{table.number}</span>
            
            {/* Capacity */}
            <div className="flex items-center gap-0.5 text-[9px] text-gray-500">
                <Users className="w-2.5 h-2.5" />
                <span>{table.capacity || 4}</span>
            </div>
            
            {/* Kitchen status */}
            {table.current_order?.kitchen_status && (
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2">
                    <ChefHat className={cn(
                        "w-3 h-3",
                        table.current_order.kitchen_status === 'ready' && "text-red-500 animate-pulse",
                        table.current_order.kitchen_status === 'preparing' && "text-amber-500",
                    )} />
                </div>
            )}
        </div>
    );
};

// Floor Plan Page Component
const FloorPlanPage = () => {
    const { api, hasPermission, user } = useAuth();
    const navigate = useNavigate();
    
    const [tables, setTables] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isEditMode, setIsEditMode] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [selectedTable, setSelectedTable] = useState(null);
    const [activeZone, setActiveZone] = useState('all');
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [newTable, setNewTable] = useState({ number: '', capacity: 4, zone: 'salle' });
    const [draggedTable, setDraggedTable] = useState(null);
    
    // Canvas dimensions
    const canvasWidth = 1200;
    const canvasHeight = 600;
    
    // Permission check
    const canEdit = hasPermission?.('manage_tables') || ['ADMIN', 'DIRECTEUR'].includes(user?.role);
    
    // Sensors for drag
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
        useSensor(KeyboardSensor)
    );
    
    // Fetch tables
    const fetchTables = useCallback(async () => {
        try {
            const res = await api().get('/tables');
            // Initialize positions if not set
            const tablesWithPositions = (res.data || []).map((table, idx) => ({
                ...table,
                position_x: table.position_x ?? (100 + (idx % 8) * 130),
                position_y: table.position_y ?? (100 + Math.floor(idx / 8) * 100),
            }));
            setTables(tablesWithPositions);
        } catch (error) {
            console.error('Error fetching tables:', error);
            toast.error('Erreur lors du chargement des tables');
        } finally {
            setLoading(false);
        }
    }, [api]);
    
    useEffect(() => {
        fetchTables();
        // Auto-refresh when not in edit mode
        const interval = setInterval(() => {
            if (!isEditMode) {
                fetchTables();
            }
        }, 15000);
        return () => clearInterval(interval);
    }, [fetchTables, isEditMode]);
    
    // Handle drag end
    const handleDragEnd = (event) => {
        const { active, delta } = event;
        
        setTables(prev => prev.map(table => {
            if (table.id === active.id) {
                const newX = Math.max(0, Math.min(canvasWidth - 64, (table.position_x || 0) + delta.x));
                const newY = Math.max(0, Math.min(canvasHeight - 64, (table.position_y || 0) + delta.y));
                return {
                    ...table,
                    position_x: newX,
                    position_y: newY,
                };
            }
            return table;
        }));
        
        setHasChanges(true);
        setDraggedTable(null);
    };
    
    const handleDragStart = (event) => {
        const table = tables.find(t => t.id === event.active.id);
        setDraggedTable(table);
    };
    
    // Save positions to backend
    const savePositions = async () => {
        try {
            const positions = tables.map(t => ({
                id: t.id,
                position_x: t.position_x,
                position_y: t.position_y,
            }));
            
            await api().post('/tables/positions', { positions });
            toast.success('Positions sauvegardées');
            setHasChanges(false);
        } catch (error) {
            console.error('Error saving positions:', error);
            toast.error('Erreur lors de la sauvegarde');
        }
    };
    
    // Reset positions
    const resetPositions = () => {
        setTables(prev => prev.map((table, idx) => ({
            ...table,
            position_x: 100 + (idx % 8) * 130,
            position_y: 100 + Math.floor(idx / 8) * 100,
        })));
        setHasChanges(true);
    };
    
    // Add new table
    const handleAddTable = async () => {
        if (!newTable.number) {
            toast.error('Numéro de table requis');
            return;
        }
        
        try {
            await api().post('/tables', {
                number: newTable.number,
                capacity: newTable.capacity,
                zone: newTable.zone,
                status: 'libre',
                position_x: 100,
                position_y: 100,
            });
            toast.success('Table ajoutée');
            setShowAddDialog(false);
            setNewTable({ number: '', capacity: 4, zone: 'salle' });
            fetchTables();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
        }
    };
    
    // Handle table selection
    const handleTableSelect = (table) => {
        if (isEditMode) return;
        
        if (table.status === 'occupee' || table.status === 'occupied') {
            navigate(`/pos?table_id=${table.id}&order_id=${table.current_order_id}`);
        } else {
            navigate(`/tables?selected=${table.id}`);
        }
    };
    
    // Filter tables by zone
    const filteredTables = activeZone === 'all' 
        ? tables 
        : tables.filter(t => t.zone?.toLowerCase() === activeZone);
    
    if (loading) {
        return (
            <div className="flex items-center justify-center h-[60vh]">
                <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
            </div>
        );
    }
    
    return (
        <div className="p-4 space-y-4" data-testid="floor-plan-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Grid className="w-6 h-6 text-blue-600" />
                        Plan de Salle
                    </h1>
                    <p className="text-gray-500 text-sm">
                        {tables.length} tables • {tables.filter(t => t.status === 'occupee' || t.status === 'occupied').length} occupées
                    </p>
                </div>
                
                <div className="flex flex-wrap gap-2">
                    {canEdit && (
                        <>
                            <Button
                                variant={isEditMode ? "default" : "outline"}
                                size="sm"
                                onClick={() => setIsEditMode(!isEditMode)}
                                className={isEditMode ? "bg-blue-600" : ""}
                            >
                                {isEditMode ? <Lock className="w-4 h-4 mr-1" /> : <Move className="w-4 h-4 mr-1" />}
                                {isEditMode ? "Verrouiller" : "Modifier"}
                            </Button>
                            
                            {isEditMode && (
                                <>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={resetPositions}
                                    >
                                        <RotateCcw className="w-4 h-4 mr-1" />
                                        Réinitialiser
                                    </Button>
                                    
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setShowAddDialog(true)}
                                    >
                                        <Plus className="w-4 h-4 mr-1" />
                                        Ajouter
                                    </Button>
                                    
                                    {hasChanges && (
                                        <Button
                                            size="sm"
                                            onClick={savePositions}
                                            className="bg-green-600 hover:bg-green-700"
                                        >
                                            <Save className="w-4 h-4 mr-1" />
                                            Sauvegarder
                                        </Button>
                                    )}
                                </>
                            )}
                        </>
                    )}
                    
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={fetchTables}
                    >
                        <RefreshCw className="w-4 h-4" />
                    </Button>
                </div>
            </div>
            
            {/* Edit mode notice */}
            {isEditMode && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm flex items-center gap-2">
                    <Move className="w-5 h-5 text-blue-600" />
                    <span className="text-blue-800">
                        <strong>Mode édition actif</strong> - Glissez-déposez les tables pour les réorganiser
                    </span>
                </div>
            )}
            
            {/* Zone filters */}
            <div className="flex flex-wrap gap-2">
                <Button
                    variant={activeZone === 'all' ? "default" : "outline"}
                    size="sm"
                    onClick={() => setActiveZone('all')}
                >
                    Toutes ({tables.length})
                </Button>
                {ZONES.map(zone => {
                    const count = tables.filter(t => t.zone?.toLowerCase() === zone.id).length;
                    const ZoneIcon = zone.icon;
                    return (
                        <Button
                            key={zone.id}
                            variant={activeZone === zone.id ? "default" : "outline"}
                            size="sm"
                            onClick={() => setActiveZone(zone.id)}
                            className={activeZone === zone.id ? zone.color : ""}
                        >
                            <ZoneIcon className="w-4 h-4 mr-1" />
                            {zone.name} ({count})
                        </Button>
                    );
                })}
            </div>
            
            {/* Floor plan canvas */}
            <Card className="overflow-hidden">
                <CardContent className="p-0">
                    <div 
                        className="relative bg-gradient-to-br from-gray-100 to-gray-200 overflow-auto"
                        style={{ 
                            width: '100%', 
                            minHeight: canvasHeight,
                            backgroundImage: 'radial-gradient(circle, #d1d5db 1px, transparent 1px)',
                            backgroundSize: '20px 20px',
                        }}
                    >
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                        >
                            <div 
                                className="relative"
                                style={{ width: canvasWidth, height: canvasHeight }}
                            >
                                {filteredTables.map(table => (
                                    <DraggableTable
                                        key={table.id}
                                        table={table}
                                        isEditMode={isEditMode}
                                        onSelect={handleTableSelect}
                                        isSelected={selectedTable?.id === table.id}
                                    />
                                ))}
                            </div>
                            
                            <DragOverlay>
                                {draggedTable && (
                                    <div className="w-16 h-16 rounded-xl border-2 border-blue-500 bg-blue-100 flex items-center justify-center shadow-xl opacity-80">
                                        <span className="text-lg font-bold">{draggedTable.number}</span>
                                    </div>
                                )}
                            </DragOverlay>
                        </DndContext>
                    </div>
                </CardContent>
            </Card>
            
            {/* Legend */}
            <div className="flex flex-wrap gap-4 justify-center">
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500" />
                    <span className="text-sm text-gray-600">Libre</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-orange-500" />
                    <span className="text-sm text-gray-600">Occupée</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-amber-500" />
                    <span className="text-sm text-gray-600">À débarrasser</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <span className="text-sm text-gray-600">Réservée</span>
                </div>
            </div>
            
            {/* Add table dialog */}
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Plus className="w-5 h-5 text-blue-600" />
                            Ajouter une table
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Numéro de table</Label>
                            <Input
                                value={newTable.number}
                                onChange={(e) => setNewTable(prev => ({ ...prev, number: e.target.value }))}
                                placeholder="Ex: 15"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Capacité (places)</Label>
                            <Input
                                type="number"
                                value={newTable.capacity}
                                onChange={(e) => setNewTable(prev => ({ ...prev, capacity: parseInt(e.target.value) || 4 }))}
                                min={1}
                                max={20}
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Zone</Label>
                            <Select
                                value={newTable.zone}
                                onValueChange={(value) => setNewTable(prev => ({ ...prev, zone: value }))}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {ZONES.map(zone => (
                                        <SelectItem key={zone.id} value={zone.id}>
                                            {zone.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                            Annuler
                        </Button>
                        <Button onClick={handleAddTable}>
                            Ajouter
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default FloorPlanPage;
